import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  InsertDriveFile,
  PictureAsPdf,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  useMediaQuery,
} from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { getToken, getUserInfo } from "../../services/auth.service";
import { getDocuments } from "../../services/documents.service";
import { getAllEvents } from "../../services/event.service";
import { getUserOnly } from "../../services/expositor/subscriptions.service";
import { useConfirm } from "../../ui-components/ConfirmProvider";
import { useToast } from "../../ui-components/ToastProvider";
import { parseDate } from "../../utils/parseDate";

export const getReceipts = async () => {
  return await (
    await fetch(`${process.env.REACT_APP_API_URL}/receipts`)
  ).json();
};

export const deleteReceipt = async (id) => {
  return await (
    await fetch(`${process.env.REACT_APP_API_URL}/receipts/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
  ).json();
};

export default function Receipts() {
  const [documents, setDocuments] = React.useState([]);
  const [receipts, setReceipts] = React.useState([]);
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const listCols = isXs ? 1 : isMd ? 2 : 4;
  const primary = theme.palette?.primary?.main || "#24324D";

  const [pendingUploads, setPendingUploads] = React.useState<
    Record<number, File[]>
  >({});
  const [uploadProgress, setUploadProgress] = React.useState<
    Record<number, number[]>
  >({});
  const [uploadErrors, setUploadErrors] = React.useState<
    Record<number, string[]>
  >({});
  const [dragActiveId, setDragActiveId] = React.useState<number | null>(null);
  const maxSizeMB = 10;
  const allowedTypes = /^(image\/.*|application\/pdf)$/;
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const addFiles = (eventId: number, files: FileList | File[]) => {
    const asArray: File[] = Array.isArray(files) ? files : Array.from(files);
    const valid: File[] = [];
    const errors: string[] = [];
    asArray.forEach((file) => {
      if (!allowedTypes.test(file.type)) {
        errors.push(`${file.name}: tipo não permitido`);
      } else if (file.size > maxSizeMB * 1024 * 1024) {
        errors.push(`${file.name}: maior que ${maxSizeMB}MB`);
      } else {
        valid.push(file);
      }
    });

    if (valid.length) {
      const prevLen = (pendingUploads[eventId] || []).length;
      setPendingUploads((prev: Record<number, File[]>) => ({
        ...prev,
        [eventId]: [...(prev[eventId] || []), ...valid],
      }));
      setUploadProgress((prev: Record<number, number[]>) => ({
        ...prev,
        [eventId]: [...(prev[eventId] || []), ...valid.map(() => 0)],
      }));
      // inicia upload automático dos arquivos recém-adicionados
      uploadPendingFilesWithProgress(eventId, valid, prevLen);
    }
    if (errors.length) {
      setUploadErrors((prev: Record<number, string[]>) => ({
        ...prev,
        [eventId]: [...(prev[eventId] || []), ...errors],
      }));
    }
  };

  const removePendingFile = (eventId: number, index: number) => {
    setPendingUploads((prev) => {
      const next = [...(prev[eventId] || [])];
      next.splice(index, 1);
      return { ...prev, [eventId]: next };
    });
    setUploadProgress((prev) => {
      const next = [...(prev[eventId] || [])];
      next.splice(index, 1);
      return { ...prev, [eventId]: next };
    });
  };

  const uploadPendingFilesWithProgress = async (
    eventId: number,
    filesOverride?: File[],
    offsetStart: number = 0
  ) => {
    const files = filesOverride || pendingUploads[eventId] || [];
    if (!files.length) return;
    try {
      await Promise.all(
        files.map(
          (file, idx) =>
            new Promise<void>((resolve, reject) => {
              const formData = new FormData();
              formData.append("user_id", String(getUserInfo().id));
              formData.append(String(eventId), file);

              const xhr = new XMLHttpRequest();
              xhr.open("POST", `${process.env.REACT_APP_API_URL}/receipts`);
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress((prev) => {
                    const arr = [...(prev[eventId] || [])];
                    arr[offsetStart + idx] = percent;
                    return { ...prev, [eventId]: arr };
                  });
                }
              };
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error("Falha no upload"));
              };
              xhr.onerror = () => reject(new Error("Falha no upload"));
              xhr.send(formData);
            })
        )
      );
      const updated = await getReceipts();
      setReceipts(updated);
      setPendingUploads((prev) => ({ ...prev, [eventId]: [] }));
      setUploadProgress((prev) => ({ ...prev, [eventId]: [] }));
      showToast("Arquivo(s) enviado(s) com sucesso!", { severity: "success" });
      // Recarrega a página ao final do processo de upload
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      console.error(err);
      showToast("Falha ao enviar arquivos. Tente novamente.", {
        severity: "error",
      });
    }
  };

  React.useEffect(() => {
    getUserOnly().then((data) => {
      setSubscriptions(data);
    });
  }, []);

  React.useEffect(() => {
    getDocuments().then((data) => {
      setDocuments(data);
    });
  }, []);

  React.useEffect(() => {
    getReceipts().then((data) => {
      setReceipts(data);
    });
  }, []);

  React.useEffect(() => {
    getAllEvents().then((data) => {
      setEvents(data);
      console.log(data, documents, subscriptions);
    });
  }, []);

  const handleDelete = async (id: number) => {
    await deleteReceipt(id);
    const updated = await getReceipts();
    setReceipts(updated);
    showToast("Arquivo excluído com sucesso!", { severity: "success" });
  };

  const eventsFromUser = new Set([...subscriptions.map((s) => s.event_id)]);

  // upload automático já atualiza a página; função antiga removida

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        boxShadow:
          "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        backgroundColor: "#ffffff",
      }}
    >
      {events &&
      events.filter((event) => eventsFromUser.has(event.id)).length > 0 ? (
        events
          .filter((event) => eventsFromUser.has(event.id))
          .map((event) => (
            <Card
              key={event.id}
              sx={{
                mb: 2,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 10px rgba(36,50,77,0.08)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                  >
                    {event.name}
                  </Typography>
                }
                subheader={`${parseDate(event.starts_at)} ate ${parseDate(
                  event.ends_at
                )}`}
                sx={{ bgcolor: "#f8fafc" }}
              />
              <CardContent>
                <Typography
                  sx={{
                    width: "100%",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 2,
                  }}
                >
                  Comprovantes deste evento
                </Typography>

                <Box>
                  {receipts &&
                  receipts.filter(
                    (doc) =>
                      doc.event_id === event.id &&
                      doc.user_id === getUserInfo().id
                  ).length > 0 ? (
                    <ImageList cols={listCols} gap={12} sx={{ m: 0 }}>
                      {receipts
                        .filter(
                          (doc) =>
                            doc.event_id === event.id &&
                            doc.user_id === getUserInfo().id
                        )
                        .map((item) => (
                          <a
                            key={item.id}
                            target={"_blank"}
                            rel="noreferrer"
                            href={item.url}
                          >
                            <ImageListItem
                              sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                boxShadow: "0 1px 4px rgba(36,50,77,0.08)",
                              }}
                            >
                              <img
                                src={
                                  item.file_type.includes("image/")
                                    ? item.url
                                    : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAbFBMVEX///8AAABLS0vc3NwwMDDo6Oi0tLTCwsJ0dHSGhoY9PT0/Pz/09PQcHBx4eHg4ODhVVVUUFBTIyMiXl5eqqqqhoaFtbW3S0tLOzs4uLi7i4uJlZWUlJSVfX19QUFCsrKwYGBiAgIBFRUW6urrFevtnAAADDElEQVR4nO3d61IaQRRF4eGiEMMtQAAxaozv/46JSf50M1xK9j5ntNb3ANOs6p7pES27aQAAAAAAAAAAAD685WrWnw9F7vvZObXlQ0/sLjupsP6m7utW4n5o6OtS4ndP3x9fs9P+2dgCO5K4MAZ2ItE5g2/S70XfPdiRxIE9MHuhmraJ7iTeRgSmJv6IKcxLHAUF5iU+1h/k6XV7c51jz+akxOpTTLfXX/LonZ2SuKs+xERwzePProx9sVpRghk8+XROSBwXH+BJcs1T+0/8Qn0qxn+WXPPkDhs+i339Ij3zDhE9i2XhQHLNM29JwbOYUBg8ixmFsbPoLxzPc2fRX/ilafmWMnAWIwonLT++xCVGFDaTzFkMKWwm07zEmMLWxKDHTVBh4ixGFebNYlhhM/mZM4txhVkLNbCwddOYS0Y8JbKwdRbtiaGFKVt/bGHGvRhcmDCL0YXxm0Z4YXhifGHT3Icm+gvvZuPS7OBXJdbElO9p2tj2xc4U9oaSsQ91p9C1UDtU2HuUjF7rUqHnbzUpfA8KKdSi8D0opFDLUXgzutT6gxZebv/pCwcUClDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaHC5y8sT3jcWcY4rjwFVXOCZq08N35tGeO48j9DLixjvASsk+N+FaO/WMaozpKNXabVUb2eFbQtB5GcB3ypSTW25hTUA9UoijOdL7SrD2QxjfNQDdNbXH0u9yW2t/164I2pcFkPlMb2DGg5EyWF63yLpnnNTvtvZCtsWs56TeA8KXBwfvgAe2NhveunML8vbrL7ejNvYP3+Hc9zwkwhdxbH/sDcezHoZ7ZB1s4/tz5FC+uWowntps9hfW9GB6/hZptlaN9fy9WsPx/6zfuzVfS3QgAAAAAAAAAAAAa/ARXQN87ZDmHrAAAAAElFTkSuQmCC"
                                }
                                alt={item.title}
                                loading="lazy"
                                style={{
                                  width: "100%",
                                  height: 180,
                                  objectFit: "cover",
                                }}
                              />
                              <ImageListItemBar
                                title={item.name}
                                subtitle={item["file_type"]}
                                sx={{
                                  background:
                                    "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.2))",
                                }}
                                actionIcon={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Chip
                                      label={
                                        item.file_type?.split("/")[1] ||
                                        "arquivo"
                                      }
                                      size="small"
                                      sx={{
                                        mr: 1,
                                        bgcolor: "rgba(255,255,255,0.2)",
                                        color: "#fff",
                                        borderColor: "rgba(255,255,255,0.4)",
                                      }}
                                    />
                                    <IconButton
                                      sx={{ color: "#fff" }}
                                      aria-label={`delete ${item.title}`}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        const ok = await confirm({
                                          title: "Excluir arquivo",
                                          description:
                                            "Você realmente deseja excluir este arquivo? Esta ação pode ser irreversível!",
                                          confirmText: "Excluir",
                                          cancelText: "Cancelar",
                                        });
                                        if (ok) {
                                          handleDelete(item.id);
                                        }
                                      }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Box>
                                }
                              />
                            </ImageListItem>
                          </a>
                        ))}
                    </ImageList>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        border: `1px dashed ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 3,
                        bgcolor: theme.palette.background.default,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <ImageIcon
                        sx={{
                          fontSize: 48,
                          color: theme.palette.primary.main,
                          mb: 1,
                        }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>
                        Nenhum comprovante encontrado para este evento
                      </Typography>
                      <Typography variant="body2">
                        Envie seus comprovantes na seção abaixo.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Typography
                  sx={{
                    width: "100%",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mt: 3,
                    mb: 1,
                  }}
                >
                  Upload de comprovantes
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: theme.palette.divider,
                  }}
                >
                  <Box
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDragEnter={() => setDragActiveId(event.id)}
                    onDragLeave={() =>
                      setDragActiveId((current) =>
                        current === event.id ? null : current
                      )
                    }
                    onDrop={(e) => {
                      e.preventDefault();
                      addFiles(event.id, e.dataTransfer.files);
                      setDragActiveId(null);
                    }}
                    onClick={() =>
                      document.getElementById(`file-input-${event.id}`)?.click()
                    }
                    sx={{
                      p: 3,
                      border: "2px dashed",
                      borderColor:
                        dragActiveId === event.id
                          ? theme.palette.primary.main
                          : theme.palette.divider,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      color: theme.palette.primary.main,
                      cursor: "pointer",
                      bgcolor:
                        dragActiveId === event.id
                          ? theme.palette.background.default
                          : theme.palette.background.paper,
                      "&:hover": { borderColor: theme.palette.primary.main },
                    }}
                  >
                    <CloudUpload
                      sx={{
                        fontSize: 40,
                        color: theme.palette.primary.main,
                        mb: 1,
                      }}
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      Arraste e solte os arquivos aqui
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, mb: 2 }}
                    >
                      ou clique para selecionar. Tipos aceitos: imagens e PDF.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Selecionar arquivos
                    </Button>
                    <input
                      id={`file-input-${event.id}`}
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        if (e.target.files) addFiles(event.id, e.target.files);
                      }}
                    />
                  </Box>

                  {Boolean((uploadErrors[event.id] || []).length) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#ef4444", fontWeight: 600 }}
                      >
                        Alguns arquivos foram ignorados:
                      </Typography>
                      {(uploadErrors[event.id] || []).map((err, idx) => (
                        <Typography
                          key={idx}
                          variant="caption"
                          sx={{ color: "#ef4444", display: "block" }}
                        >
                          • {err}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {Boolean((pendingUploads[event.id] || []).length) && (
                    <Box sx={{ mt: 2 }}>
                      {(pendingUploads[event.id] || []).map((file, idx) => (
                        <Box key={idx}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 1.5,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                              mb: 1,
                              bgcolor: theme.palette.background.paper,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {file.type === "application/pdf" ? (
                                <PictureAsPdf
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              ) : file.type.startsWith("image/") ? (
                                <ImageIcon
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              ) : (
                                <InsertDriveFile
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              )}
                              <Chip
                                label={`${
                                  file.type?.split("/")[1] || "arquivo"
                                }`}
                                size="small"
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.primary }}
                              >
                                {file.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                {(file.size / 1024).toFixed(1)} KB
                              </Typography>
                            </Box>
                            <IconButton
                              aria-label="remover"
                              onClick={() => removePendingFile(event.id, idx)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(uploadProgress[event.id] || [])[idx] || 0}
                            sx={{ mb: 1 }}
                          />
                        </Box>
                      ))}

                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}
                        disabled={
                          !Boolean((pendingUploads[event.id] || []).length)
                        }
                        onClick={() => uploadPendingFilesWithProgress(event.id)}
                        fullWidth
                      >
                        Enviar comprovantes
                      </Button>
                    </Box>
                  )}
                </Paper>
              </CardContent>
            </Card>
          ))
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            border: "1px dashed #e2e8f0",
            borderRadius: 2,
            p: 3,
            bgcolor: "#f8fafc",
            color: "#64748b",
          }}
        >
          <CloudUpload
            sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }}
          />
          <Typography sx={{ fontWeight: 600 }}>
            Nenhum evento encontrado
          </Typography>
          <Typography variant="body2">
            Você ainda não possui eventos associados.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
